import type { SDKModule, SDKContext } from '../types';
import { closestFrom } from '../utils/dom';
import { log } from '../utils/logger';

/**
 * Form Lifecycle Module
 *
 * Tracks form_start (first focus) and form_abandon (page unload without submit).
 * All events go through ctx.track, so they are preview-aware automatically.
 */
export function createFormLifecycle(): SDKModule {
  let ctx: SDKContext;
  const formStarted: Record<string, boolean> = {};
  const formSubmitted: Record<string, boolean> = {};

  let focusHandler: ((e: Event) => void) | null = null;
  let unloadHandler: (() => void) | null = null;
  let submittedHandler: ((data?: unknown) => void) | null = null;

  function handleFocus(e: Event): void {
    const target = e.target as EventTarget | null;
    if (!target) return;
    const wrapper = closestFrom(target, '[data-pt-component="dynamic-form"]');
    if (!wrapper) return;
    const form = wrapper.querySelector('form');
    if (!form) return;

    const formId = form.getAttribute('data-pt-id') || form.id || '';
    if (formStarted[formId]) return;
    formStarted[formId] = true;

    ctx.track('form_start', { form_id: formId });
  }

  function handleBeforeUnload(): void {
    for (const formId in formStarted) {
      if (formStarted[formId] && !formSubmitted[formId]) {
        try {
          ctx.track('form_abandon', { form_id: formId });
        } catch (e) {
          log('warn', 'form_abandon track failed', e);
        }
      }
    }
  }

  return {
    name: 'form-lifecycle',
    init(context: SDKContext): void {
      ctx = context;

      submittedHandler = (data?: unknown) => {
        const formId = (data as { formId?: string } | undefined)?.formId;
        if (formId) formSubmitted[formId] = true;
      };
      ctx.on('form:submitted', submittedHandler);

      focusHandler = handleFocus;
      document.addEventListener('focusin', focusHandler, true);

      unloadHandler = handleBeforeUnload;
      window.addEventListener('beforeunload', unloadHandler);
    },
    destroy(): void {
      if (focusHandler) {
        document.removeEventListener('focusin', focusHandler, true);
        focusHandler = null;
      }
      if (unloadHandler) {
        window.removeEventListener('beforeunload', unloadHandler);
        unloadHandler = null;
      }
      if (submittedHandler) {
        ctx.off('form:submitted', submittedHandler);
        submittedHandler = null;
      }
    },
  };
}
